/*
 * NORTHVALE PATCH — original shader raindrop canvas
 * Preserves the project-14070659 shader core (StaticDrops, three DropLayer2
 * layers, refraction normals, textureLod focus, FBM fog, ping-pong wipe mask,
 * gradual re-fog). Northvale tuning:
 *  - rainAmount baseline 0.40 (lighter rain)
 *  - brush radius ~26vh (bigger wipe area)
 *  - fade ~0.9917/frame → ~6s to fully re-fog
 */
import { useEffect, useRef } from "react";

const VERTEX_SHADER = `#version 300 es
in vec2 a_position;
out vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const MASK_FRAGMENT = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;

uniform sampler2D uPrev;
uniform vec2  uBrushStart;
uniform vec2  uBrushEnd;
uniform float uBrushRadius;
uniform float uFade;
uniform float uHasBrush;
uniform float uAspect;

float segDist(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  float denom = max(dot(ba, ba), 1e-6);
  float h = clamp(dot(pa, ba) / denom, 0.0, 1.0);
  return length(pa - ba * h);
}

void main() {
  float mask = texture(uPrev, v_uv).r;
  mask *= uFade;

  if (uHasBrush > 0.5) {
    vec2 p  = vec2(v_uv.x * uAspect, v_uv.y);
    vec2 a  = vec2(uBrushStart.x * uAspect, uBrushStart.y);
    vec2 b  = vec2(uBrushEnd.x   * uAspect, uBrushEnd.y);
    float d = segDist(p, a, b);
    float paint = smoothstep(uBrushRadius, uBrushRadius * 0.25, d);
    mask = max(mask, paint);
  }

  outColor = vec4(mask, 0.0, 0.0, 1.0);
}
`;

const RAIN_FRAGMENT = `#version 300 es
precision highp float;
in  vec2 v_uv;
out vec4 fragColor;

uniform vec2  iResolution;
uniform float iTime;
uniform sampler2D iChannel0;
uniform sampler2D uWipeMask;
uniform float uImageAspect;
uniform float uFocusX;
uniform float uFocusY;

#define S(a, b, t) smoothstep(a, b, t)

vec3 N13(float p) {
  vec3 p3 = fract(vec3(p) * vec3(0.1031, 0.11369, 0.13787));
  p3 += dot(p3, p3.yzx + 19.19);
  return fract(vec3((p3.x + p3.y) * p3.z,
                    (p3.x + p3.z) * p3.y,
                    (p3.y + p3.z) * p3.x));
}

float N(float t) { return fract(sin(t * 12345.564) * 7658.76); }
float Saw(float b, float t) { return S(0.0, b, t) * S(1.0, b, t); }

float hash2(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash2(i);
  float b = hash2(i + vec2(1.0, 0.0));
  float c = hash2(i + vec2(0.0, 1.0));
  float d = hash2(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 3; i++) {
    v += valueNoise(p) * amp;
    p *= 2.0;
    amp *= 0.5;
  }
  return v;
}

vec2 DropLayer2(vec2 uv, float t, float seed) {
  vec2 UV = uv;
  vec2 a = vec2(7.5, 1.0);
  vec2 grid = a * 2.0;

  float colId = floor(uv.x * grid.x);
  float colSpeed = 0.7 + N(colId * 13.37 + seed * 5.5) * 0.9;
  uv.y += t * 0.65 * colSpeed;

  vec2 id = floor(uv * grid);
  float colShift = N(id.x + seed * 91.3);
  uv.y += colShift;

  id = floor(uv * grid);
  vec3 n  = N13(id.x * 35.2 + id.y * 2376.1 + seed * 17.0);
  vec3 n2 = N13(id.x * 91.7 + id.y * 521.3  + seed * 41.0);
  vec2 st = fract(uv * grid) - vec2(0.5, 0.0);

  float exists = step(0.22, n2.y);

  float x = n.x - 0.5;
  float y = UV.y * 20.0;
  float wiggle = sin(y + sin(y));
  float wiggleAmt = 0.2 + n2.x * 0.6;
  x += wiggle * (0.5 - abs(x)) * (n.z - 0.5) * wiggleAmt;
  x *= 0.5;

  float ti = fract(t + n.z + n2.z * 0.7);
  y = (Saw(0.85, ti) - 0.5) * 0.9 + 0.5;
  vec2 p = vec2(x, y);

  float size = 0.18 + n2.x * 0.22;
  float speedNow = abs(0.5 - ti) * 2.0;
  float squish = mix(1.0, 0.6, speedNow * n2.y);
  vec2 ellip = vec2(1.0, squish);

  float d = length((st - p) * a.yx * ellip);
  float mainDrop = S(size, 0.0, d) * exists;

  float r = sqrt(S(1.0, y, st.y));
  float cd = abs(st.x - x);
  float trailWidth = (0.18 + n2.z * 0.12) * r;
  float trail = S(trailWidth, trailWidth * 0.65, cd);
  float trailFront = S(-0.02, 0.02, st.y - y);
  trail *= trailFront * r * r * exists;

  y = UV.y;
  float trail2 = S(0.2 * r, 0.0, cd);
  float droplets = max(0.0, (sin(y * (1.0 - y) * (90.0 + n2.z * 80.0)) - st.y))
                   * trail2 * trailFront * n.z;
  y = fract(y * (8.0 + n2.x * 6.0)) + (st.y - 0.5);
  float dd = length(st - vec2(x, y));
  droplets = S(0.3, 0.0, dd) * exists;
  float m = mainDrop + droplets * r * trailFront;

  return vec2(m, trail);
}

float StaticDrops(vec2 uv, float t) {
  uv.y += t * 0.18;
  uv *= 55.0;
  vec2 id = floor(uv);
  uv = fract(uv) - 0.5;
  vec3 n  = N13(id.x * 107.45 + id.y * 3543.654);
  vec3 n2 = N13(id.x * 17.31  + id.y * 88.97);

  vec2 p = (n.xy - 0.5) * 0.7;
  p.y -= 0.15 * fract(t * (0.3 + n2.x * 0.4) + n.z);

  float sizeR = 0.18 + n2.y * 0.18;
  vec2  ellip = vec2(1.0, 0.8 + n2.z * 0.7);
  float d = length((uv - p) * ellip);

  float exists = step(0.32, n2.x);

  float fade = Saw(0.025, fract(t + n.z));
  float c = S(sizeR, 0.0, d) * fract(n.z * 10.0) * fade * exists;
  return c;
}

vec2 Drops(vec2 uv, float t, float l0, float l1, float l2) {
  float s = StaticDrops(uv, t) * l0;
  vec2 m1 = DropLayer2(uv,         t, 0.0) * l1;
  vec2 m2 = DropLayer2(uv * 1.85,  t, 7.3) * l2;
  vec2 m3 = DropLayer2(uv * 0.65 + vec2(0.31, 0.0), t, 21.7) * l1 * 0.65;

  float c = s + m1.x + m2.x + m3.x;
  c = S(0.3, 1.0, c);

  return vec2(c, max(max(m1.y * l0, m2.y * l1), m3.y * l0));
}

void main() {
  vec2 fragCoord = gl_FragCoord.xy;
  vec2 uv = (fragCoord - 0.5 * iResolution) / iResolution.y;
  vec2 UV = fragCoord / iResolution;

  float T = iTime;
  float t = T * 0.14;

  // Northvale: lighter baseline rain (0.45 -> 0.40)
  float rainAmount = sin(T * 0.05) * 0.15 + 0.40;
  // No base blur — image stays fully crisp; droplets only refract, never blur
  float maxBlur = 0.0;
  float minBlur = 0.0;

  float zoom = -0.5;
  uv *= 0.7 + zoom * 0.3;

  // object-cover math: preserve image aspect, crop to canvas, anchor at focus
  float canvasAspect = iResolution.x / iResolution.y;
  vec2 scale = vec2(1.0);
  vec2 offset = vec2(0.0);
  if (canvasAspect > uImageAspect) {
    // canvas wider than image -> fit width, crop vertically
    scale.y = uImageAspect / canvasAspect;
    offset.y = (1.0 - scale.y) * (1.0 - uFocusY);
  } else {
    // canvas taller/narrower -> fit height, crop horizontally
    scale.x = canvasAspect / uImageAspect;
    offset.x = (1.0 - scale.x) * uFocusX;
  }
  vec2 bgUV = UV * scale + offset;

  float staticDrops = S(-0.5, 1.0, rainAmount) * 2.0;
  float layer1 = S(0.25, 0.75, rainAmount);
  float layer2 = S(0.0, 0.5, rainAmount);

  vec2 c = Drops(uv, t, staticDrops, layer1, layer2);
  vec2 e = vec2(0.001, 0.0);
  float cx = Drops(uv + e,   t, staticDrops, layer1, layer2).x;
  float cy = Drops(uv + e.yx,t, staticDrops, layer1, layer2).x;
  vec2 n = vec2(cx - c.x, cy - c.x);

  float wipe = texture(uWipeMask, v_uv).r;
  wipe = clamp(wipe, 0.0, 1.0);

  c *= (1.0 - wipe);
  n *= (1.0 - wipe);

  float focus = mix(maxBlur - c.y, minBlur, S(0.1, 0.2, c.x));
  focus = mix(focus, 0.0, wipe);

  vec3 col = textureLod(iChannel0, bgUV + n, 0.0).rgb;

  // Fog layer removed — keep only a very subtle vignette for depth
  float vig = smoothstep(1.2, 0.4, length(UV - 0.5));
  col *= mix(0.78, 1.0, vig);

  fragColor = vec4(col, 1.0);
}
`;

function compile(gl: WebGL2RenderingContext, type: number, src: string) {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(sh));
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

function link(gl: WebGL2RenderingContext, vs: WebGLShader, fs: WebGLShader) {
  const p = gl.createProgram();
  if (!p) return null;
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error(gl.getProgramInfoLog(p));
    return null;
  }
  return p;
}

interface FBO {
  fb: WebGLFramebuffer;
  tex: WebGLTexture;
}

function createFBO(gl: WebGL2RenderingContext, w: number, h: number): FBO | null {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const fb = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  if (!fb || !tex) return null;
  return { fb, tex };
}

interface Props {
  backgroundUrl: string;
  className?: string;
}

const MASK_SIZE = 768;
// Northvale: wider wipe (~26vh) and slower refog (~6s @ 60fps to full)
const BRUSH_RADIUS_UV = 0.26;
const FADE_PER_FRAME = 0.998;

export default function WeatherGlass({ backgroundUrl, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const stateRef = useRef({
    curX: -10,
    curY: -10,
    prevX: -10,
    prevY: -10,
    hasInput: 0,
    everMoved: false,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl2", {
      antialias: false,
      premultipliedAlpha: false,
      powerPreference: "high-performance",
    });
    if (!gl) {
      console.warn("WebGL2 not supported");
      return;
    }

    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const rainFs = compile(gl, gl.FRAGMENT_SHADER, RAIN_FRAGMENT);
    const maskFs = compile(gl, gl.FRAGMENT_SHADER, MASK_FRAGMENT);
    if (!vs || !rainFs || !maskFs) return;

    const rainProg = link(gl, vs, rainFs);
    const maskProg = link(gl, vs, maskFs);
    if (!rainProg || !maskProg) return;

    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );

    const bindQuad = (prog: WebGLProgram) => {
      const loc = gl.getAttribLocation(prog, "a_position");
      gl.bindBuffer(gl.ARRAY_BUFFER, quad);
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    };

    let fboA = createFBO(gl, MASK_SIZE, MASK_SIZE);
    let fboB = createFBO(gl, MASK_SIZE, MASK_SIZE);
    if (!fboA || !fboB) return;

    [fboA, fboB].forEach((f) => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, f.fb);
      gl.viewport(0, 0, MASK_SIZE, MASK_SIZE);
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
    });

    const bgTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, bgTex);
    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGBA,
      1,
      1,
      0,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      new Uint8Array([18, 18, 28, 255]),
    );
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageAspect = img.naturalWidth / Math.max(img.naturalHeight, 1);
      gl.bindTexture(gl.TEXTURE_2D, bgTex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    };
    img.src = backgroundUrl;

    const u_rain = {
      res: gl.getUniformLocation(rainProg, "iResolution"),
      time: gl.getUniformLocation(rainProg, "iTime"),
      tex: gl.getUniformLocation(rainProg, "iChannel0"),
      mask: gl.getUniformLocation(rainProg, "uWipeMask"),
      imgAspect: gl.getUniformLocation(rainProg, "uImageAspect"),
      focusX: gl.getUniformLocation(rainProg, "uFocusX"),
      focusY: gl.getUniformLocation(rainProg, "uFocusY"),
    };
    let imageAspect = 16 / 9;
    const u_mask = {
      prev: gl.getUniformLocation(maskProg, "uPrev"),
      bStart: gl.getUniformLocation(maskProg, "uBrushStart"),
      bEnd: gl.getUniformLocation(maskProg, "uBrushEnd"),
      bRad: gl.getUniformLocation(maskProg, "uBrushRadius"),
      fade: gl.getUniformLocation(maskProg, "uFade"),
      has: gl.getUniformLocation(maskProg, "uHasBrush"),
      aspect: gl.getUniformLocation(maskProg, "uAspect"),
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.8);
      const w = Math.floor(canvas.clientWidth * dpr);
      const h = Math.floor(canvas.clientHeight * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const host = canvas.parentElement ?? canvas;
    const onMove = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = 1 - (e.clientY - rect.top) / rect.height;
      const s = stateRef.current;
      if (!s.everMoved) {
        s.prevX = x;
        s.prevY = y;
        s.everMoved = true;
      }
      s.curX = x;
      s.curY = y;
      s.hasInput = 1;
    };
    const onLeave = () => {
      stateRef.current.hasInput = 0;
    };
    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerleave", onLeave);

    let visible = true;
    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) visible = en.isIntersecting;
      },
      { threshold: 0 },
    );
    io.observe(canvas);

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const start = performance.now();
    let raf = 0;
    let firstFrameDispatched = false;

    const render = () => {
      if (!visible) {
        raf = requestAnimationFrame(render);
        return;
      }
      const now = performance.now();
      const t = (now - start) / 1000;
      const s = stateRef.current;

      gl.bindFramebuffer(gl.FRAMEBUFFER, fboB!.fb);
      gl.viewport(0, 0, MASK_SIZE, MASK_SIZE);
      gl.useProgram(maskProg);
      bindQuad(maskProg);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, fboA!.tex);
      gl.uniform1i(u_mask.prev, 0);

      const aspect = canvas.clientWidth / Math.max(canvas.clientHeight, 1);
      gl.uniform2f(u_mask.bStart, s.prevX, s.prevY);
      gl.uniform2f(u_mask.bEnd, s.curX, s.curY);
      gl.uniform1f(u_mask.bRad, BRUSH_RADIUS_UV);
      gl.uniform1f(u_mask.fade, FADE_PER_FRAME);
      gl.uniform1f(u_mask.has, s.hasInput && s.everMoved ? 1 : 0);
      gl.uniform1f(u_mask.aspect, aspect);

      gl.drawArrays(gl.TRIANGLES, 0, 6);

      const tmp = fboA;
      fboA = fboB;
      fboB = tmp;

      s.prevX = s.curX;
      s.prevY = s.curY;

      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.useProgram(rainProg);
      bindQuad(rainProg);

      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, bgTex);
      gl.uniform1i(u_rain.tex, 0);

      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, fboA!.tex);
      gl.uniform1i(u_rain.mask, 1);

      gl.uniform2f(u_rain.res, canvas.width, canvas.height);
      gl.uniform1f(u_rain.time, reduceMotion ? 0 : t);
      gl.uniform1f(u_rain.imgAspect, imageAspect);
      // Match Hero <img> object-position: 70% on mobile, 65% on md+
      const focusX = window.innerWidth >= 768 ? 0.65 : 0.70;
      gl.uniform1f(u_rain.focusX, focusX);
      gl.uniform1f(u_rain.focusY, 0.5);

      gl.drawArrays(gl.TRIANGLES, 0, 6);

      if (!firstFrameDispatched) {
        firstFrameDispatched = true;
        try { window.dispatchEvent(new Event("nv:weatherglass-ready")); } catch { /* ignore */ }
      }

      if (reduceMotion) return;
      raf = requestAnimationFrame(render);
    };
    render();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
      gl.deleteBuffer(quad);
      gl.deleteTexture(bgTex);
      [fboA, fboB].forEach((f) => {
        if (!f) return;
        gl.deleteFramebuffer(f.fb);
        gl.deleteTexture(f.tex);
      });
      gl.deleteProgram(rainProg);
      gl.deleteProgram(maskProg);
      gl.deleteShader(vs);
      gl.deleteShader(rainFs);
      gl.deleteShader(maskFs);
    };
  }, [backgroundUrl]);

  return <canvas ref={canvasRef} className={className} />;
}